import BranchMergeEventFactory from "./BranchMergeEventFactory";
import DesignerSetting from "../../DesignerSetting";
import { DesignBranchingEventType } from "../../types";
import {
	addBranchingEvent,
	Case,
	convertCase,
	createUuid,
	incrementVersion
} from "../functions";
import { defaultServiceContext } from "../../contexts";
import { createDesignChangeMap } from './functions';

/**
 * JSDoc imports.
 * @import { AvsChangeLogs, Design } from "../../types";
 */

const moment = require("moment");
const { avsService } = defaultServiceContext;

export class BranchManager {
	/** Instantiate BranchManager Class */
	constructor() {
		this.branchMergeEventFactory = new BranchMergeEventFactory();
	}

	/**
	 * @param {Design} mainBranch to create a branch from.
	 * @param {string} branchName for the newly created branch.
	 * @param {number} timestamp for the branch's creation.
	 */
	getInitializedBranch(mainBranch, branchName, timestamp) {
		const branchId = createUuid();
		const author = window.apm.getDesignerAuthor();
		const branch = _.cloneDeep(mainBranch);

		delete branch.branching;
		delete branch.branchingMap;
		delete branch["$loki"];

		branch._id = branchId;
		branch.branchId = branchId;
		branch.branchName = branchName;
		branch.version = "1.0.0";
		branch.repoVersion = "1.0.0";
		branch.applicationVersion = branch.applicationVersion.replace(
			/^\s*([0-9\.]+).*/,
			`$1-${convertCase(branchName, Case.snake).toUpperCase()}-SNAPSHOT`
		);
		addBranchingEvent(branch, this.branchMergeEventFactory.getEvent(
			DesignBranchingEventType.initBranch,
			{ mainBranch, timestamp, author }
		));

		return branch;
	}

	/**
	 * @param {Design} mainBranch
	 * @param {string} branchName
	 * @param {string} author
	 * @param {boolean} [isHotfix]
	 */
	async initFeatureBranch(mainBranch, branchName, author, isHotfix = false) {
		const timestamp = (window && window.apm && window.apm.timestamp) ? window.apm.timestamp : moment.now(); 
		const mainOriginal = _.cloneDeep(mainBranch);
		// initialize branch
		const featureBranch = this.getInitializedBranch(mainBranch, branchName, timestamp);

		const commitMessage = `Branch created: ${featureBranch.branchName} from ${mainBranch.branchName} version ${mainBranch.version}`;

		const savedFrom = mainBranch.version;

		/** @type {AvsChangeLogs[]} */
		const featureBranchChangeLogMap = [
			{
				author,
				isCollaborationBranch: true,
				version: featureBranch.version,
				changeLogMap: [
					createDesignChangeMap(featureBranch, mainOriginal),
					{}
				],
				commitMessage,
				d2commit: DesignerSetting.s.commit,
				env: DesignerSetting.s.env,
				timeStamp: timestamp,
				savedFrom
			}
		];
		
		// When branching from old version, skip Main update
		if (isHotfix) {
			return { 
				mainBranch: null, 
				mainBranchChangeLogMap: null, 
				featureBranch, 
				featureBranchChangeLogMap 
			};
		}
		
		// initialize/update main branch (normal flow when on latest version)
		const {
			branchId,
			version: originalVersion
		} = mainBranch;

		mainBranch.branchingMap = mainBranch.branchingMap || {};
		addBranchingEvent(mainBranch, this.branchMergeEventFactory.getEvent(
			DesignBranchingEventType.initBranchingMap,
			{ mainBranch: mainBranch, featureBranch, timestamp, author }
		));

		const newVersion = incrementVersion(mainBranch.version);
		mainBranch.version = newVersion;
		mainBranch.repoVersion = newVersion;

		const mainBranchChangeLogEntry = this.getChangeLogEntry({
			author,
			version: mainBranch.version,
			changeLogMap: [
				createDesignChangeMap(mainBranch, mainOriginal),
				{}
			],
			reason: "Modified",
			commitMessage,
			d2commit: DesignerSetting.s.commit,
            env: DesignerSetting.s.env,
			savedFrom
		});

		const mainBranchChangeLogMap = await avsService.getChangeLogs(branchId, originalVersion);
		mainBranchChangeLogMap.unshift(mainBranchChangeLogEntry);

		return { mainBranch, mainBranchChangeLogMap, featureBranch, featureBranchChangeLogMap };
	}

	getChangeLogEntry(obj) {
		const { author, latestVersion, version, saveAsNewVersion, changeLogMap, initial, commitMessage, savedFrom } = obj;

		return window.apm.getChangeLogEntry({ author, latestVersion, version, saveAsNewVersion, changeLogMap, initial, commitMessage, savedFrom });
	}
}
